"use strict";
/**
 * Gas-type helpers — single source of truth for which gas codes are
 * valid and which kind (refrigerant vs fugitive) each one is.
 *
 * Two distinct enums exist for historical/scientific reasons:
 *   - `RefrigerantGasType` — HFCs, HCFCs, natural refrigerants used
 *     in cooling systems (R134A, R410A, etc.). Routed to the activity
 *     model's `refrigerantType` field.
 *   - `FugitiveGasType` — high-GWP industrial process gases (SF6, NF3,
 *     PFCs). Routed to the activity model's `gasType` field.
 *
 * The wizard's UI surfaces both as one combined "gas" picker since
 * users pick a gas without knowing the underlying enum split. Submit
 * code uses `isFugitiveGas(value)` to route the picked value into the
 * right field.
 *
 * **Use these types/lists at every site that holds a gas value** —
 * seed scripts, import-template dropdowns, activity-config maps. The
 * compiler will refuse any literal that isn't in the enums, so a
 * hand-edited subset can never silently drift away from the canonical
 * set (the same drift-prevention pattern as `Region`).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isFugitiveGas = exports.VALID_GASES = void 0;
const enums_1 = require("./enums");
/**
 * Runtime list of every valid gas literal. Typed as `readonly Gas[]`
 * so any `includes` / `Object.values` consumer stays narrow.
 */
exports.VALID_GASES = [
    ...Object.values(enums_1.RefrigerantGasType),
    ...Object.values(enums_1.FugitiveGasType),
];
/**
 * Pre-computed Set of fugitive-gas literals — `O(1)` membership check
 * for the submit-time routing decision (`refrigerantType` vs
 * `gasType` field on the activity model).
 */
const FUGITIVE_GAS_VALUES = new Set(Object.values(enums_1.FugitiveGasType));
/**
 * Routes a gas literal to the right activity-model field. Used by the
 * wizard's submit to split a single `gas` picker value back into the
 * server's distinct `refrigerantType` / `gasType` shape.
 */
const isFugitiveGas = (value) => FUGITIVE_GAS_VALUES.has(value);
exports.isFugitiveGas = isFugitiveGas;
